import { setAuthTokenGetter } from '@/api/http/axios-client';
import { isConnectionError } from '@/api/http/errors';
import { auth } from '@/config/firebase-config';
import { clearSavedQueriesStorage } from '@/features/firestore/query-builder/query-storage';
import { clearExplorerTabsStorage } from '@/features/firestore/tabs-storage';
import { gcpKeys } from '@/query/gcp';
import { googleKeys, useLogoutAndForget, useOauthStatus, useSyncUser } from '@/query/google';
import type { LogoutForgetResponse } from '@firestore-gui/shared';
import { OAuthStatusResponse } from '@firestore-gui/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  GoogleAuthProvider,
  User,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const DISCONNECTED_STATUS: OAuthStatusResponse = {
  status: 'disconnected',
  connectedAt: null,
  lastRefreshAt: null,
  scopes: [],
  googleAccountEmail: undefined,
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  oauthStatus: OAuthStatusResponse | null;
  refreshOAuthStatus: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  logoutAndForget: () => Promise<LogoutForgetResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const { data: oauthStatusData, refetch: refetchOauthStatus } = useOauthStatus({
    enabled: !!user,
  });
  const logoutAndForgetMutation = useLogoutAndForget();

  const syncUserMutation = useSyncUser();
  const syncUserMutationRef = useRef(syncUserMutation);
  syncUserMutationRef.current = syncUserMutation;

  const oauthStatus = oauthStatusData ?? null;

  /**
   * Retry a function with exponential backoff
   */
  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxRetries: number = 5,
    initialDelay: number = 500
  ): Promise<T> => {
    let lastError: unknown;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        // Only retry on connection errors
        if (!isConnectionError(error) || attempt === maxRetries - 1) {
          throw error;
        }
        // Exponential backoff: 500ms, 1s, 2s, 4s, 8s
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(
          `Backend not ready, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    throw lastError;
  };

  const refreshOAuthStatus = useCallback(async () => {
    try {
      await refetchOauthStatus();
    } catch (error) {
      console.error('Error fetching OAuth status:', error);
      queryClient.setQueryData(googleKeys.oauthStatus(), DISCONNECTED_STATUS);
    }
  }, [queryClient, refetchOauthStatus]);

  /**
   * Sync user with backend, with retry logic.
   * Uses a ref for the mutation so this callback stays stable and does not
   * retrigger the onAuthStateChanged effect (which would re-run sync on every render).
   */
  const syncUserWithBackend = useCallback(
    async (firebaseUser: User): Promise<void> => {
      try {
        await retryWithBackoff(() =>
          syncUserMutationRef.current.mutateAsync({
            uid: firebaseUser.uid,
            email: firebaseUser.email ?? null,
            displayName: firebaseUser.displayName ?? null,
            photoURL: firebaseUser.photoURL ?? null,
          })
        );

        await retryWithBackoff(async () => {
          await refetchOauthStatus();
        });
      } catch (error) {
        console.error('Error syncing user with backend after retries:', error);
        if (isConnectionError(error)) {
          queryClient.setQueryData(googleKeys.oauthStatus(), DISCONNECTED_STATUS);
        }
      }
    },
    [queryClient, refetchOauthStatus]
  );

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          setToken(idToken);
          queryClient.invalidateQueries({ queryKey: googleKeys.oauthStatus() });
          queryClient.invalidateQueries({ queryKey: gcpKeys.activeConnection() });

          await syncUserWithBackend(firebaseUser);
        } catch (error) {
          console.error('Error getting token:', error);
          setToken(null);
        }
      } else {
        setToken(null);
      }
      setLoading(false);
    });

    setAuthTokenGetter(async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }
      try {
        return await currentUser.getIdToken();
      } catch (error) {
        console.error('Error getting auth token for API:', error);
        return null;
      }
    });

    return () => unsubscribe();
  }, [syncUserWithBackend]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');

    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      setToken(credential?.accessToken ?? null);
      setUser(result.user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await firebaseSignOut(auth);
      queryClient.clear();
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logoutAndForget = async (): Promise<LogoutForgetResponse> => {
    setLoading(true);
    try {
      const result = await logoutAndForgetMutation.mutateAsync();

      localStorage.removeItem('lastConnectionId');
      clearExplorerTabsStorage();
      clearSavedQueriesStorage();

      await firebaseSignOut(auth);
      queryClient.clear();
      setUser(null);
      setToken(null);
      return result;
    } catch (error) {
      console.error('Error logging out and forgetting:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    oauthStatus,
    refreshOAuthStatus,
    signInWithGoogle,
    signOut,
    logoutAndForget,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
